
import { useState, FormEvent } from 'react';

interface HeroProps {
  isSubmitted: boolean;
  setIsSubmitted: (value: boolean) => void;
}

export default function Hero({ isSubmitted, setIsSubmitted }: HeroProps) {
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('phoneNumber', formData.phoneNumber);
      formBody.append('email', formData.email);
      const response = await fetch('https://readdy.ai/api/form/d6e92e2tehdqnvnpjafg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ companyName: '', firstName: '', lastName: '', phoneNumber: '', email: '' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/10d9254ed20057d911efa7445b05da13.jpg"
          alt="Professional business consultation environment"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/80"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16 items-center">
          {/* Left — Copy */}
          <div>
            <div className="inline-block bg-blue-600/20 border border-blue-400/30 backdrop-blur-sm text-blue-300 px-5 py-2 rounded-full text-sm font-medium mb-8">
              <i className="ri-shield-star-line mr-2"></i>
              15+ Years of Trusted Broker Solutions
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              <span className="block">Turn Bad Leads Into</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                Real Commissions
              </span>
            </h1>

            <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-xl">
              Still holding{' '}
              <strong className="text-white">bad leads</strong>,{' '}
              <strong className="text-white">delinquencies</strong>, or{' '}
              <strong className="text-white">judgments</strong>? Our broker referral
              program turns your dead files into revenue. Confidential, fast, and
              proven.
            </p>

            <div className="grid grid-cols-3 gap-4 max-w-md">
              <div className="bg-white/8 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-white mb-0.5">Millions</div>
                <div className="text-gray-400 text-xs">Paid Out</div>
              </div>
              <div className="bg-white/8 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-white mb-0.5">15+</div>
                <div className="text-gray-400 text-xs">Years Exp.</div>
              </div>
              <div className="bg-white/8 backdrop-blur-md rounded-xl p-4 border border-white/10 text-center">
                <div className="text-3xl font-bold text-white mb-0.5">5000+</div>
                <div className="text-gray-400 text-xs">Cases Done</div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white rounded-2xl p-8 shadow-2xl lg:ml-auto w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-900 mb-1">Get More Information</h3>
            <p className="text-gray-500 text-sm mb-5">
              Fill out the form and our team will reach out within 24 hours.
            </p>

            {isSubmitted && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-5 text-sm flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-green-500"></i>
                Thank you! Your information has been submitted successfully.
              </div>
            )}

            <form
              id="inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-3.5"
            >
              <div>
                <label htmlFor="companyName" className="block text-xs font-semibold text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  placeholder="Your Company LLC"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm"
                />
                {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-xs font-semibold text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-xs font-semibold text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Smith"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm"
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="phoneNumber" className="block text-xs font-semibold text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm"
                />
                {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer mt-1"
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Get Started'}
              </button>

              <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                <i className="ri-lock-line"></i>
                Your information is secure and confidential.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
